const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
}

const getDefaultLogLevel = () => {
  const envLogLevel = process.env.VUE_APP_LOG_LEVEL || process.env.NUXT_PUBLIC_LOG_LEVEL
  if (envLogLevel && LOG_LEVELS[envLogLevel] !== undefined) {
    return LOG_LEVELS[envLogLevel]
  }
  return process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG
}

const DEFAULT_LOG_LEVEL = getDefaultLogLevel()

const LOG_FILE_MAX_DAYS = 7
const logQueue = []
let logWorker = null

if (typeof window !== 'undefined' && window.Worker) {
  try {
    logWorker = new Worker('/workers/log-writer.js')
  } catch (e) {
    console.warn('Log worker not available, logs will not be persisted to file')
  }
}

class Logger {
  constructor(component, logLevel = DEFAULT_LOG_LEVEL) {
    this.component = component
    this.logLevel = logLevel
  }

  _log(level, levelName, color, ...args) {
    if (level < this.logLevel) return

    const timestamp = new Date().toISOString()
    const prefix = `[${timestamp}] [${this.component}]`

    if (process.client && console[level === LOG_LEVELS.DEBUG ? 'log' : levelName.toLowerCase()]) {
      console[level === LOG_LEVELS.DEBUG ? 'log' : levelName.toLowerCase()](
        `%c${prefix}`,
        `color: ${color}; font-weight: bold`,
        ...args
      )
    } else {
      console.log(prefix, `[${levelName}]`, ...args)
    }

    if (level >= LOG_LEVELS.WARN && logWorker) {
      const logEntry = {
        timestamp,
        component: this.component,
        level: levelName,
        message: args.map(arg =>
          typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
        ).join(' '),
      }
      logWorker.postMessage({ type: 'log', entry: logEntry })
    }
  }

  debug(...args) {
    this._log(LOG_LEVELS.DEBUG, 'DEBUG', '#6c757d', ...args)
  }

  info(...args) {
    this._log(LOG_LEVELS.INFO, 'INFO', '#0dcaf0', ...args)
  }

  warn(...args) {
    this._log(LOG_LEVELS.WARN, 'WARN', '#ffc107', ...args)
  }

  error(...args) {
    this._log(LOG_LEVELS.ERROR, 'ERROR', '#dc3545', ...args)
  }

  success(...args) {
    this._log(LOG_LEVELS.INFO, 'SUCCESS', '#198754', '✅', ...args)
  }

  event(eventName, payload) {
    this._log(LOG_LEVELS.DEBUG, 'EVENT', '#0d6efd', `📡 ${eventName}`, payload)
  }

  socket(action, ...args) {
    this._log(LOG_LEVELS.DEBUG, 'SOCKET', '#6610f2', `🔌 ${action}`, ...args)
  }

  setLevel(level) {
    this.logLevel = level
  }
}

export function createLogger(component) {
  return new Logger(component)
}

export { LOG_LEVELS }
